/**
 * THE COMMERCIAL MODEL, IN ONE PLACE.
 *
 * Every number a buyer can read on the gateway resolves to a constant in this
 * file, and every one of these constants also appears in the Enterprise
 * Subscription Terms. That is the whole point of the file: the four documents
 * (pricing page, Terms, Data Sharing Addendum, sales script) have already
 * contradicted each other once on renewal price, data retention and pilot
 * length, and the binding number turned out to be the least generous one. One
 * definition, generated onto every surface, is the fix.
 *
 * ── The pricing shape, and why it is not per seat ─────────────────────────
 * A capacity commitment the buyer chooses, not a metered claim count.
 *
 * A per-seat price meters a number produced by student behaviour in a free
 * consumer app. The buyer cannot forecast it, cannot audit it, and cannot
 * control it, and it goes up when their own advisors succeed at the campaign
 * P3 asked them to run. There is also a harder reason, which is not published:
 * the claim count is written by the client, so it is not a number anyone can
 * invoice on. Capacity removes the whole class of problem. It deletes the
 * overage state machine, the seat recount as a billing input, proration
 * pinning, the tier cliff, and the seat validator along with it.
 *
 * P3 does not meter claims for billing. That sentence is on the page.
 *
 * ── What the Worker serves ────────────────────────────────────────────────
 * The capability list a customer reads is the Worker's manifest
 * (src/worker/lib/capabilities.ts), served at GET /api/enterprise/capabilities
 * and on the session, and rendered on Settings under Data and methods through
 * `capabilityColumnsFrom` (section 10). CAPABILITY_TABLE (section 4) is a
 * typed copy in the build plan's three columns that no page renders; a change
 * to what P3 offers goes in the manifest first.
 */

import { K } from "~/lib/suppression";
import { formatCount, formatMoney } from "~/app/lib/format";

/* ==========================================================================
   1. Bands

   The ladder is MONTHLY FIRST. Thomas's reason is sticker shock and it is
   correct: $4,000 reads as a purchase, $400 reads as a subscription. Annual
   is exactly ten times monthly, which is the discount stated as a mechanism
   ("two months free") rather than as a percentage nobody can check.

   The per-student figure is derived, never typed, so the descent 27, 23, 20,
   18 cannot drift from the prices above it.
   ========================================================================== */

/**
 * The band identifiers are the WIRE FORMAT. A checkout request, a quote
 * request and a Stripe Price lookup key are all keyed on these strings, so
 * they are named after the capacity rather than the price: a price can be
 * repriced, a capacity cannot.
 */
export type BandId =
  | "band_150"
  | "band_300"
  | "band_600"
  | "band_1000"
  | "quote";

export interface CapacityBand {
  readonly id: BandId;
  /** The name a requisition carries. */
  readonly name: string;
  /** The short form the slider chip shows. */
  readonly shortName: string;
  /** The ceiling of the commitment. null on the quoted band. */
  readonly capacity: number | null;
  /** Monthly fee in whole US dollars. THE HERO FIGURE. null when quoted. */
  readonly monthly: number | null;
  /** Annual fee in whole US dollars, exactly ten times monthly. null when quoted. */
  readonly annual: number | null;
  /** Who the band is shaped for. At most twelve words, and no fabricated norms. */
  readonly fit: string;
  /**
   * The lookup keys of the band's two Stripe Prices, one per billing interval
   * (Sep 14 2026: the eight Prices in Stripe carry `p3_capacity_<band>_<month|year>_v1`).
   * The Worker finds a Price by this key and never by amount. null when quoted.
   */
  readonly priceKeys: { readonly month: string; readonly year: string } | null;
}

/**
 * The months a buyer does not pay for on the annual plan. Annual is
 * `monthly * (12 - ANNUAL_FREE_MONTHS)`, and a test asserts it, so the claim
 * on the page is the arithmetic rather than a sentence beside it.
 */
export const ANNUAL_FREE_MONTHS = 2;
export const ANNUAL_PAID_MONTHS = 12 - ANNUAL_FREE_MONTHS;

export const BANDS: readonly CapacityBand[] = [
  {
    id: "band_150",
    name: "Up to 150 students",
    shortName: "150 students",
    capacity: 150,
    monthly: 400,
    annual: 4000,
    fit: "One program, one office, or one class year.",
    priceKeys: { month: "p3_capacity_150_month_v1", year: "p3_capacity_150_year_v1" },
  },
  {
    id: "band_300",
    name: "Up to 300 students",
    shortName: "300 students",
    capacity: 300,
    monthly: 700,
    annual: 7000,
    fit: "Several programs, or one college within a university.",
    priceKeys: { month: "p3_capacity_300_month_v1", year: "p3_capacity_300_year_v1" },
  },
  {
    id: "band_600",
    name: "Up to 600 students",
    shortName: "600 students",
    capacity: 600,
    monthly: 1200,
    annual: 12000,
    fit: "A school, or a district's junior and senior years.",
    priceKeys: { month: "p3_capacity_600_month_v1", year: "p3_capacity_600_year_v1" },
  },
  {
    id: "band_1000",
    name: "Up to 1,000 students",
    shortName: "1,000 students",
    capacity: 1000,
    monthly: 1800,
    annual: 18000,
    fit: "A campus-wide commitment, or a multi-site district.",
    priceKeys: { month: "p3_capacity_1000_month_v1", year: "p3_capacity_1000_year_v1" },
  },
  {
    id: "quote",
    name: "More than 1,000 students",
    shortName: "Over 1,000",
    capacity: null,
    monthly: null,
    annual: null,
    fit: "A system, a consortium, or a multi-campus agreement.",
    priceKeys: null,
  },
];

/** The lowest band. The marketing line "starting at $400 a month" is this one. */
export const ENTRY_BAND: CapacityBand = BANDS[0]!;

export const DEFAULT_BAND_ID: BandId = "band_150";

export function bandById(id: BandId): CapacityBand {
  const found = BANDS.find((b) => b.id === id);
  // BandId is closed over BANDS, so this is unreachable. It exists because
  // noUncheckedIndexedAccess is on and a non-null assertion would be a lie.
  return found ?? BANDS[BANDS.length - 1]!;
}

/**
 * The slider carries an INDEX into BANDS, taken from Sentry, so a price that
 * is not on the rate card is unreachable by construction rather than
 * validated after the fact. Both directions live here so the control cannot
 * invent its own mapping.
 */
export function bandIndexOf(id: BandId): number {
  const at = BANDS.findIndex((b) => b.id === id);
  return at === -1 ? 0 : at;
}

export function bandAtIndex(index: number): CapacityBand {
  const clamped = Math.min(Math.max(Math.trunc(index), 0), BANDS.length - 1);
  return BANDS[clamped] ?? BANDS[0]!;
}

/** The smallest band that covers a cohort of `students`. */
export function bandForStudents(students: number): CapacityBand {
  for (const band of BANDS) {
    if (band.capacity !== null && students <= band.capacity) return band;
  }
  return bandById("quote");
}

/**
 * The price of a cohort, in one call, for anything that needs the whole offer
 * rather than the band object: the band, the monthly figure, the annual
 * figure and the derived per-student year rate, with null where the answer is
 * "we quote it".
 *
 * A cohort of zero or fewer students is not a smaller purchase, it is a
 * question nobody asked, so it resolves to the entry band rather than to a
 * price of nothing.
 */
export interface Price {
  readonly band: CapacityBand;
  readonly monthly: number | null;
  readonly annual: number | null;
  readonly perStudentYear: number | null;
  readonly quoted: boolean;
}

export function priceFor(students: number): Price {
  const safe = Number.isFinite(students) ? Math.max(1, Math.trunc(students)) : 1;
  return priceOf(bandForStudents(safe));
}

export function priceOf(band: CapacityBand): Price {
  return {
    band,
    monthly: band.monthly,
    annual: band.annual,
    perStudentYear: perStudentEquivalent(band),
    quoted: band.annual === null,
  };
}

/** Every legal band id, for validating one that arrived from a query string. */
export const BAND_IDS: readonly BandId[] = BANDS.map((b) => b.id);

/** Narrows an untrusted string to a band. Returns null rather than guessing. */
export function bandFromWire(input: unknown): CapacityBand | null {
  if (typeof input !== "string") return null;
  return BANDS.find((b) => b.id === input) ?? null;
}

/**
 * The per-student equivalent, in dollars per student per year, rounded to the
 * whole dollar the page prints. It falls as capacity rises (27, 23, 20, 18)
 * and that descent is the argument for the larger band.
 *
 * It is an equivalent, never a rate. Nothing is metered per student and the
 * copy never calls this a price per student on its own.
 */
export function perStudentEquivalent(band: CapacityBand): number | null {
  if (band.annual === null || band.capacity === null) return null;
  return Math.round(band.annual / band.capacity);
}

/** The two billing cadences a buyer can pick. Structurally the same union as
 *  CapacitySlider's and checkout-model's `Billing`, so either passes here. */
export type Cadence = "monthly" | "annual";

/**
 * The same equivalent per student per MONTH, rounded to the cent: 2.67, 2.33,
 * 2.00, 1.80. The Monthly view prints it in place of the yearly figure
 * (Thomas, Sep 14 2026). Cents rather than whole dollars on purpose: whole
 * dollars would print $3, $2, $2, $2, which flattens the descent that argues
 * for the larger band and puts "$3" on the page beside a stat that says
 * "Under $3".
 */
export function perStudentMonthlyEquivalent(band: CapacityBand): number | null {
  if (band.monthly === null || band.capacity === null) return null;
  return Math.round((band.monthly / band.capacity) * 100) / 100;
}

/** The per-student figure in the cadence on screen: "$2.67" monthly, "$27" annual. Null when quoted. */
export function formatPerStudent(band: CapacityBand, cadence: Cadence): string | null {
  if (cadence === "annual") {
    const per = perStudentEquivalent(band);
    return per === null ? null : formatMoney(per);
  }
  const per = perStudentMonthlyEquivalent(band);
  return per === null ? null : formatMoney(per, { cents: true });
}

/** "$2.67 per student, per month" or "$27 per student, per year". Null on the quoted band. */
export function describeEquivalence(band: CapacityBand, cadence: Cadence): string | null {
  const per = formatPerStudent(band, cadence);
  if (per === null) return null;
  return `${per} per student, per ${cadence === "annual" ? "year" : "month"}`;
}

/**
 * The whole-number percentage a buyer keeps by paying annually: twelve
 * monthly payments against the annual price, rounded. 17 on every priced
 * band today (annual is ten times monthly), computed rather than typed so a
 * band whose ladder ever differs reports its own figure. Null on the quoted
 * band, and null if a band ever priced annual at or above twelve months.
 * Worn by the Save tag beside the billing toggle (Thomas, Sep 9 2026).
 */
export function annualSavingPercent(band: CapacityBand): number | null {
  if (band.monthly === null || band.annual === null) return null;
  const yearOfMonths = band.monthly * 12;
  if (yearOfMonths <= band.annual) return null;
  return Math.round(((yearOfMonths - band.annual) / yearOfMonths) * 100);
}

/**
 * What a screen reader hears on every step of the slider. Sentry's
 * aria-valuetext carries the whole offer rather than "3 of 5", and it is
 * built from the rate card here so it cannot drift from the figure on screen.
 */
export function describeBandForAria(band: CapacityBand, cadence: Cadence): string {
  if (band.monthly === null || band.annual === null) {
    return `${band.name}, price by quote`;
  }
  if (cadence === "annual") {
    return `${band.name}, ${formatMoney(band.annual)} per year, ${formatPerStudent(band, "annual")} per student per year`;
  }
  return `${band.name}, ${formatMoney(band.monthly)} per month, ${formatPerStudent(band, "monthly")} per student per month`;
}

/** The one-sentence line a printed page carries in place of the slider, in the cadence on screen. */
export function describeBandForPrint(band: CapacityBand, cadence: Cadence): string {
  if (band.monthly === null || band.annual === null) {
    return `Capacity commitment: ${lowerFirstLetter(band.name)}. Priced on your cohort.`;
  }
  return cadence === "annual"
    ? `Capacity commitment: ${lowerFirstLetter(band.name)}. ${formatMoney(band.annual)} per year, billed annually, ${formatPerStudent(band, "annual")} per student per year.`
    : `Capacity commitment: ${lowerFirstLetter(band.name)}. ${formatMoney(band.monthly)} per month, billed monthly, ${formatPerStudent(band, "monthly")} per student per month.`;
}

function lowerFirstLetter(text: string): string {
  return text.charAt(0).toLowerCase() + text.slice(1);
}

/**
 * The headroom sentence inside a chosen band. Null when nothing was typed.
 *
 * The unit agrees with its own number. Typing 149 into the 150 band leaves one
 * student of headroom, and "1 students of headroom" was on the rendered page
 * until September 8 2026: it is reachable at every band ceiling minus one, so
 * it is a sentence a buyer typing their real cohort can easily land on.
 */
export function describeHeadroom(
  band: CapacityBand,
  students: number | null,
): string | null {
  if (students === null || band.capacity === null) return null;
  if (students > band.capacity) {
    return `${formatCount(students)} ${students === 1 ? "student is" : "students are"} above this band. ${bandForStudents(students).name} covers it.`;
  }
  const spare = band.capacity - students;
  if (spare === 0) {
    return `${formatCount(students)} of ${formatCount(band.capacity)} students included, with no headroom left.`;
  }
  return `${formatCount(students)} of ${formatCount(band.capacity)} students included, and ${formatCount(spare)} ${spare === 1 ? "student" : "students"} of headroom.`;
}

/**
 * The only validation the model needs, now that blocks and minimums are gone.
 * The Worker runs the same function on every path that can set a band.
 */
export function isSelfServe(band: CapacityBand): boolean {
  return band.annual !== null;
}

export const MAX_TYPED_STUDENTS = 250_000;

/** Parses what a buyer typed. Commas, spaces and a stray plus are all fine. */
export function parseStudentCount(raw: string): number | null {
  const cleaned = raw.replace(/[,\s+]/g, "");
  if (cleaned === "") return null;
  if (!/^\d+$/.test(cleaned)) return null;
  const n = Number(cleaned);
  if (!Number.isFinite(n) || n < 0 || n > MAX_TYPED_STUDENTS) return null;
  return Math.trunc(n);
}

/* ==========================================================================
   1b. ONE VALUE, TWO CONTROLS

   THE BUG THIS EXISTS TO KILL. Until September 8 2026 the gateway held the
   band and the typed cohort as two independent pieces of state. Typing moved
   the band; moving the band did not touch the typed number. So a buyer who
   typed 150 and then dragged to the 600 band was looking at a card that said
   "150 students" in its own field and "Up to 600" on its own badge: the page
   stated two different covered capacities at once, and neither control
   admitted the other existed. Thomas found it and called it a bug, which it
   is.

   THE FIX IS NOT A SYNC. Two values kept in step by an effect is the same
   bug with a race in it. There is ONE value here, `CapacityChoice`, and the
   two controls are two ways of writing it:

     · the slider writes it through `capacityFromSlider`, which sets the
       cohort to the band's own ceiling. That is the only number a band can
       claim on its own, so the field can never show one the ladder
       contradicts.
     · the field writes it through `capacityFromTyped`, which puts the band
       at the smallest one that covers what was typed. Typing 742 lands on
       "Up to 1,000" because that is the band that covers 742.

   Both directions are asserted in tests/pricing.test.ts, driving the slider
   through all five stops and typing into the field, and the rendering is
   asserted through the real component with react-dom/server.

   THE QUOTED BAND HAS NO CEILING, so it has no number to show. The cohort
   goes to null there and the field falls back to a placeholder that says
   "Over 1,000", which is a statement of the band rather than a figure that
   could disagree with it. Everything else on that band is quoted too.
   ========================================================================== */

export interface CapacityChoice {
  readonly bandId: BandId;
  /** The cohort in the field. null only on the quoted band, or when cleared. */
  readonly students: number | null;
}

/**
 * First paint. The cohort is deliberately null rather than 150: nothing has
 * been chosen yet, the field shows its placeholder, and the note slot under
 * the ladder stays empty instead of opening with a sentence about headroom
 * the buyer never asked for. The moment either control is touched the pair
 * is written together and stays together.
 */
export const DEFAULT_CAPACITY: CapacityChoice = {
  bandId: DEFAULT_BAND_ID,
  students: null,
};

/** The slider moved. The cohort follows the band to the band's own ceiling. */
export function capacityFromSlider(index: number): CapacityChoice {
  const band = bandAtIndex(index);
  return { bandId: band.id, students: band.capacity };
}

/**
 * Someone typed. The band follows to the smallest one that covers it.
 *
 * An empty or unparseable field does NOT move the band: a buyer part way
 * through selecting and retyping a number would otherwise watch the ladder
 * jump to the entry band between keystrokes.
 */
export function capacityFromTyped(
  raw: string,
  current: CapacityChoice,
): CapacityChoice {
  const parsed = parseStudentCount(raw);
  if (parsed === null || parsed === 0) {
    return { bandId: current.bandId, students: null };
  }
  return { bandId: bandForStudents(parsed).id, students: parsed };
}

/** What the field renders. Empty is not a claim, so it is allowed to be. */
export function cohortFieldValue(choice: CapacityChoice): string {
  return choice.students === null ? "" : formatCount(choice.students);
}

/**
 * What the empty field says. It is always the band's own ceiling, so an empty
 * field still cannot disagree with the ladder, and it is drawn in the faint
 * ink so it reads as a prompt rather than as a figure somebody entered.
 *
 * The quoted band has no ceiling, so it borrows the label the ladder's last
 * scale mark already carries: "1,000+". The badge above the thumb says "Over
 * 1,000" in full because it has the room; the field does not. It is 5.5ch
 * wide, sized to the widest real cohort ("1,000"), and "Over 1,000" rendered
 * into it clipped mid-glyph, which was caught on a render and not in the
 * source.
 */
export function cohortFieldPlaceholder(choice: CapacityChoice): string {
  const band = bandById(choice.bandId);
  return band.capacity === null
    ? `${formatCount(1000)}+`
    : formatCount(band.capacity);
}

/**
 * The one line under the ladder.
 *
 * It is the RECONCILIATION between the two controls, so it says nothing when
 * there is nothing to reconcile: no cohort typed, or a cohort sitting exactly
 * on the band's ceiling because the slider put it there. Printing "300 of 300
 * students included, with no headroom left" on every stop of the ladder would
 * turn the slider's own output into a warning.
 */
export function describeCapacityNote(choice: CapacityChoice): string | null {
  const band = bandById(choice.bandId);
  if (choice.students === null) return null;
  if (choice.students === band.capacity) return null;
  return describeHeadroom(band, choice.students);
}

/* ==========================================================================
   2. Contract constants

   Every one of these appears in the Terms. A CI check asserts that any number
   in gateway copy or an email template resolves to a name in this block.
   ========================================================================== */

export const TERM_MONTHS = 12;
/** Notice P3 sends before a renewal date. Both land before the deadline below. */
export const RENEWAL_NOTICE_DAYS = [90, 45] as const;
/** How long before term end the institution must say it is not renewing. */
export const CANCELLATION_NOTICE_DAYS = 30;
/** Either party, any reason, on notice. Terms 16.3. */
export const CONVENIENCE_NOTICE_DAYS = 60;
/** Cure period on a material breach. Terms 16.2. */
export const CURE_PERIOD_DAYS = 30;
/** Export-only access after the agreement ends. Terms 16.5. */
export const EXPORT_RETENTION_MONTHS = 12;
/** Email support target. A target, not a guarantee. Terms 10.2. */
export const SUPPORT_RESPONSE_BUSINESS_DAYS = 2;
/** How long a written quote holds. */
export const QUOTE_VALID_DAYS = 30;
/** How long P3 takes to return a quote. */
export const QUOTE_RESPONSE_BUSINESS_DAYS = 1;
/** Invoice terms on the purchase-order path. */
export const INVOICE_NET_DAYS = 30;
/** Notice before a new subprocessor starts processing. DSA Section X. */
export const SUBPROCESSOR_NOTICE_DAYS = 30;
/** The privacy floor. Contractual, and it is not a setting. DSA Section V. */
export const SUPPRESSION_FLOOR = K;

export const LEGAL = {
  entity: "The Pulse of Perseverance Project, Inc.",
  /* The name the IRS holds against the EIN, as line 1 of the W-9 and the
     determination letter print it. A payables office matches a vendor's W-9
     to the IRS record, so the contract names it too (Thomas, Sep 13 2026:
     both names at the start of the Terms, "to ensure coverage"). */
  nonprofitEntity: "Pulse of Perseverance",
  status: "501(c)(3) nonprofit corporation",
  incorporation: "Illinois",
  ein: "82-3649154",
  address: "4522 S Prairie Avenue, Chicago, Illinois 60653",
  /* LOUISIANA, NOT ILLINOIS (Thomas, Sep 12 2026), matching what he confirmed
     for the Institutional Partnership Agreement Suite on June 24 2026: P3 is
     incorporated in Illinois and keeps Louisiana law because it has teams
     there. Incorporation stays Illinois; the two are different fields.

     FOR COUNSEL, AND NOT A FIND-AND-REPLACE. Louisiana is a civil law state,
     unlike every other US state. The warranty disclaimer in Section 14 is
     common law drafting, Louisiana carries its own warranty against
     redhibitory defects, and the jury waiver and the liability cap were
     drafted with Illinois in mind. Whether each still operates as written is
     counsel's call. The venue parish below is a DRAFT choice (P3's Louisiana
     roots are in Baton Rouge), not a decision anyone has made. */
  governingLaw: "Louisiana",
  venue: "East Baton Rouge Parish, Louisiana",
  contactEmail: "team@pulseofp3.org",
  termsVersion: "2026.2",
  termsUrl: "/legal/enterprise-terms/2026-2",
  dsaVersion: "2026.2",
  dsaUrl: "/legal/data-sharing-addendum/2026-2",
  privacyUrl: "https://www.pulseofp3.org/privacy",
  w9Url: "/documents/p3-w9-2026.pdf",
} as const;

/* ==========================================================================
   3. Mentor supply

   Published because it is the binding constraint on the whole product, and
   because a customer discovering an undisclosed, undersized mentor pool is
   the one thing here that could genuinely embarrass P3. The no-match line is
   not a disclaimer bolted on, it is the finding.
   ========================================================================== */

export const MENTOR_SUPPLY = {
  approvedMentors: 154,
  mentorships: 526,
  studentsOnPlatform: 955,
  asOf: "September 7, 2026",
  cadence: "Recounted weekly.",
  noGuarantee:
    "P3 does not guarantee that any student is matched with a mentor. Approved mentors serve every institution on P3, not one institution at a time, and a match depends on a mentor choosing to take a student on.",
} as const;

/**
 * Career pathways a student can explore with no mentor and no age gate.
 *
 * Counted, not estimated: p3-web's `features/resources` renders DEMO_PATHWAYS,
 * which is the nine-key guide corpus (pathways.json) plus eight pathways
 * authored in TypeScript. Nine plus eight. If that corpus grows, this figure
 * and the hero move together, so it is a constant here rather than a literal
 * typed into a stat row.
 */
export const PATHWAY_COUNT = 17;

/** The largest institutional cohort on P3 today. Sets the buyer's expectation. */
export const LARGEST_COHORT_TODAY = 56;

/* ==========================================================================
   4. The honest capability statement

   Build Plan v1.0, section 4: three columns, and the third carries the same
   visual weight as the other two on purpose. It is the single most
   trust-building thing a university buyer can read, and P3 has a documented
   history of the opposite behaviour.

   No page renders this constant (Sep 12 2026). Settings renders the Worker's
   manifest through `capabilityColumnsFrom` instead, so a customer never reads
   a list typed twice. It stays because claims.test.ts scans it with every
   other absence list; if a page ever shows these three columns, render them
   from GET /api/enterprise/capabilities, not from here.
   ========================================================================== */

export type CapabilityColumnId = "day-one" | "by-90-days" | "not-offered";

export interface CapabilityColumn {
  readonly id: CapabilityColumnId;
  readonly eyebrow: string;
  readonly heading: string;
  readonly note: string;
  readonly items: readonly string[];
}

export const CAPABILITY_TABLE: readonly CapabilityColumn[] = [
  {
    id: "day-one",
    eyebrow: "Day one",
    heading: "What you get on day one",
    note: "Available the hour your subscription starts.",
    items: [
      "A join link, a six character code, and a QR code, generated before the welcome screen paints.",
      "Unlimited free administrator accounts, in three roles: owner, admin and viewer.",
      "Unlimited free mentor accounts, and your students keep the free P3 app whether or not you ever subscribe.",
      `The W-9, EIN ${LEGAL.ein}, the IRS determination letter, the Data Sharing Addendum and the subprocessor list with data regions at permanent addresses, and a HECVAT completed for your institution on request.`,
      `A dashboard that is honest about being empty. Reporting begins at ${SUPPRESSION_FLOOR} claimed students, and the screen says so with a progress meter.`,
      `A named P3 contact and a ${SUPPORT_RESPONSE_BUSINESS_DAYS} business day response target on email support.`,
    ],
  },
  {
    id: "by-90-days",
    eyebrow: "By 90 days",
    heading: "What you get by 90 days, if the claim campaign runs",
    note: "Each figure appears once enough students have claimed you.",
    items: [
      "Your claimed roster count, and a weekly growth curve anchored to Sunday, so a partial week never reads as a collapse.",
      "Students with at least one active mentorship, as a rate with its numerator, its denominator and a 95% interval.",
      "Students who asked for a mentor and have not been matched yet, which is the most operationally useful figure on the panel.",
      `What your students say they want help with, their education goal, industry interest and meeting rhythm, each group suppressed below ${SUPPRESSION_FLOOR}.`,
      "A CSV export with a methods manifest, and a print ready briefing you can save as PDF and forward to your provost.",
      "A comparison of your claimed students against all P3 students in aggregate, labeled as a comparison between groups and never as an effect P3 caused.",
    ],
  },
  {
    id: "not-offered",
    eyebrow: "Not offered",
    heading: "What P3 explicitly does not offer",
    note: "We would rather you read this here than find it in a security review.",
    items: [
      "A verified outcome record. Graduation, major, first role and employer reach P3 only as a student reports them, in a milestone or their own career timeline, and P3 does not verify them against your registrar or an employer. There is no GPA, credit hours, enrollment status, salary or credential award from an awarding body.",
      "Any dollar figure: return on investment, cost avoided, cost per outcome, hiring cost. P3 holds no cost data for any institution.",
      "Predictive risk scores, early alerts, or student level flags.",
      "Individual student records, or any export containing one. This is deliberate and permanent, because it is what makes the suppression commitment true.",
      "A named peer institution, or a third party benchmark. Your cohort is compared against all P3 students in aggregate, never against a named institution or a national or regional average.",
      "Single sign-on (SAML, OIDC, SCIM), a public API, or any student information system or learning management system connection.",
      "Data that updates as you watch. The dashboard recomputes hourly and the header carries the actual timestamp.",
      "A service level agreement, an uptime commitment, or service credits.",
      "SOC 2, and a third party penetration test report. P3 holds neither and will not represent that it does.",
      `A guarantee that any student is matched with a mentor. P3 has ${formatCount(MENTOR_SUPPLY.approvedMentors)} approved mentors serving every institution, we publish that number, and we do not promise a match.`,
      "Verification that a student who claims your institution actually attends it. Affiliation is student asserted.",
      "Roster upload, or a list of your students in any other form. Students add your institution themselves, and our Data Sharing Addendum does not claim school official status under FERPA.",
      "Demographics, race, ethnicity, first generation status, Pell eligibility, or student geography. None is collected.",
    ],
  },
];

/** The two lines that sit under the table, in the same type size as the table. */
export const CAPABILITY_FOOTNOTES: readonly string[] = [
  `Totals appear once ${SUPPRESSION_FLOOR} students have claimed you. Breakdowns appear per group at ${SUPPRESSION_FLOOR}, with complementary suppression, so a full set of breakdowns needs somewhere between 40 and 100 claimed students. The largest institutional cohort on P3 today is ${LARGEST_COHORT_TODAY} students.`,
  "Onboarding is work your team does. Your advisors send the email, your office prints the QR code, your orientation gives up a slide. Expect to see nothing for two to six weeks, and expect the result to depend on student behavior neither of us can compel.",
];

/* ==========================================================================
   5. What the commitment covers
   ========================================================================== */

export interface IncludedRow {
  readonly capability: string;
  readonly detail: string;
}

export const INCLUDED: readonly IncludedRow[] = [
  {
    capability: "One dashboard, scoped to your institution",
    detail:
      "Your claimed roster, how it is growing, how many students have been matched, and what those students say they want help with.",
  },
  {
    capability: "Administrator accounts",
    detail:
      "Unlimited, at no cost, in three roles: owner, admin and viewer. You invite and remove them yourself.",
  },
  {
    capability: "Mentor accounts",
    detail: "Unlimited, at no cost, and never counted against your commitment.",
  },
  {
    capability: "Student accounts",
    detail:
      "Free to every student on iOS, Android and the web, forever, whether or not your institution subscribes. Your subscription buys your institution a dashboard, not your students' access.",
  },
  {
    capability: "Your claim artifacts",
    detail:
      "A join link, a six character code and a QR code, plus an advisor email you can copy and send.",
  },
  {
    capability: "Export",
    detail:
      "CSV files of your own aggregates with a methods manifest, and a print ready briefing you can save as PDF.",
  },
  {
    capability: "Procurement pack",
    detail: `W-9, EIN ${LEGAL.ein}, IRS determination letter, the Data Sharing Addendum and subprocessor list at permanent addresses, the Certificate of Insurance on request, and a HECVAT completed for your institution.`,
  },
  {
    capability: "Support",
    detail: `Email, with a ${SUPPORT_RESPONSE_BUSINESS_DAYS} business day response target, and a named P3 contact.`,
  },
];

/** The three paragraphs that explain what a capacity commitment is. */
export const WHAT_A_COMMITMENT_IS: readonly string[] = [
  "A capacity commitment is the size of program you are buying reporting for. You choose the band that fits your program, and that band is the whole price. There is no per student meter, no seat counter running against you, and no invoice that changes because your students used the app more than you expected.",
  "P3 does not meter claims for billing. Students claim your institution themselves, inside a free app, and that number moves for reasons neither of us controls. Billing on it would hand you a bill written by student behavior, which is not a number a purchasing office can approve in advance.",
  "If more students claim you than your band covers, nothing stops and no student is blocked. P3 does not send you an unexpected invoice and does not raise your price mid-term. We look at it with you at renewal, and if the number is genuinely larger we will say so.",
];

/* ==========================================================================
   5b. The gateway's own copy

   The rebuilt gateway is held to a 250 to 400 word budget for the whole
   visible page. Adobe sells a $70/month subscription in 211 words on
   adobe.com/products/photoshop/plans.html, and Linear covers four plans plus
   a ninety-row capability matrix in 322. The long-form versions of everything
   below still exist above and on /trust; these are the short ones the gateway
   itself renders.

   Every string here is checked against the standing rules: no em dash, no
   "most partners", "typically", "on average", "usually" or "within their
   first", "students" rather than "mentees", and no claim about observed
   customer experience, because there are zero paying partners and therefore
   no experience to describe.
   ========================================================================== */

export interface ShortRow {
  readonly title: string;
  /** At most twelve words. Adobe's own median plan description is ten. */
  readonly detail: string;
}

/**
 * WHAT A STUDENT GETS, AND WHY IT IS FIRST.
 *
 * Every earlier draft of this page sold 1:1 matching, which capped the
 * product's value at mentor capacity and made every unmatched student read as
 * a failure. Thomas repositioned it on September 8, 2026: a student gets real
 * value on day one whether or not a mentor ever takes them on, so mentor
 * supply is not a ceiling on how many students an institution can onboard.
 *
 * EVERY ROW HERE WAS READ OUT OF p3-web BEFORE IT WAS WRITTEN DOWN, because a
 * suite claim is the easiest kind of claim to inflate:
 *   · Opportunities      features/opportunities, a filtered feed of approved
 *                        listings with expired deadlines gated out.
 *   · Career pathways    features/resources -> DEMO_PATHWAYS, which is nine
 *                        corpus pathways plus eight authored ones. Seventeen.
 *                        No age gate on that route.
 *   · Pulse AI           features/coach, 18+ at the route (`if (!isAdult)`
 *                        returns before the chat component mounts) and again
 *                        at the proxy, answering from a curated link
 *                        allowlist rather than from model-invented URLs.
 *   · CV review          features/career, same 18+ gate, plus the timeline
 *                        built from the uploaded CV.
 *   · Open questions     features/ask, where a student with no mentor is
 *                        switched to the open pool and any approved mentor
 *                        answers. NAMED "Open", NOT "Public": the audience is
 *                        P3's approved mentors, not the internet, and "public"
 *                        oversells it in the one direction a buyer would mind.
 *   · Video answers      features/videos, the Featured feed, open to all.
 *
 * THE TWO 18+ SURFACES SAY SO ON THE CARD. A university buyer who discovers
 * an age gate after signing has been misled; one who reads it here has been
 * told. It is also the only honest way to write "every student" above.
 */
export const STUDENT_SUITE: readonly ShortRow[] = [
  {
    title: "Opportunities",
    detail: "Scholarships, internships and jobs. 200+ new every month.",
  },
  {
    title: "Career pathways",
    detail: "Seventeen fields, the roles inside each, and the routes in.",
  },
  {
    title: "Open questions",
    detail: "Ask without a match. Any approved mentor can answer.",
  },
  {
    title: "Video answers",
    detail: "Short videos from mentors, open to every student to browse.",
  },
  {
    title: "Pulse AI",
    detail: "Career coaching, interview practice and CV review. 18 and older.",
  },
  /* NO MENTORSHIP CARD. Removed September 8 2026 at Thomas's request, the
     second of two moves reducing its prominence on this page, and consistent
     with the repositioning: what a student gets does not wait on a match.

     THE DISCLOSURE DID NOT GO WITH IT. MENTOR_SUPPLY's figures and the
     no-guarantee line now live in the FAQ ("Do you guarantee every student a
     mentor?"), which is where a buyer hunting for the catch actually looks,
     and in NOT_OFFERED on /trust. Do not let a future tidy-up drop them: they
     are what makes the rest of this page believable. */
];

/** What the INSTITUTION gets. Rendered under STUDENT_SUITE, never above it. */
export const GATEWAY_INCLUDED: readonly ShortRow[] = [
  {
    title: "Your dashboard",
    detail: "Claimed roster, growth, matches, and what students want help with.",
  },
  {
    title: "Your join kit",
    detail: "A link, a six character code, and a QR code.",
  },
  {
    title: "Export",
    detail: "CSV files with a methods manifest, and a printable briefing.",
  },
  {
    title: "Procurement pack",
    detail: "W-9, IRS letter, insurance, HECVAT, and the subprocessor list.",
  },
  {
    title: "Support",
    detail: `Email, a named contact, and a ${SUPPORT_RESPONSE_BUSINESS_DAYS} business day response target.`,
  },
];

/**
 * The short form of CAPABILITY_TABLE's third column. Same thirteen absences,
 * in the same order, phrased for a scan rather than for a security review.
 * The long form is one click away on /trust, which is the standard a
 * procurement officer is entitled to.
 */
export const NOT_OFFERED_SHORT: readonly string[] = [
  "No SOC 2, and no third party penetration test",
  "No single sign-on, and no public API",
  "No student information system or LMS connection",
  "No verified outcomes. Graduation and first role are student volunteered",
  "No dollar figures, and no return on investment",
  "No predictive risk scores or student level flags",
  "No contact details for any student, and no roster from your systems",
  "No named peer institutions, and no third party benchmark data",
  "No data that updates as you watch. The dashboard recomputes hourly",
  "No service level agreement and no uptime commitment",
  "No enrollment verification. Affiliation is student asserted",
  "No guarantee that any student is matched with a mentor",
  "No demographics, and no student geography",
];

/**
 * Mentor supply, written as SCOPE rather than as an apology.
 *
 * The facts did not change and the disclosure did not weaken: P3 still does
 * not guarantee a match, and that absence is still item twelve of
 * NOT_OFFERED_SHORT and still on /trust. What changed is the frame. A student
 * with no mentor is using P3, not queuing for it, so these lines say what is
 * open to them rather than what they are missing.
 */
export const MENTOR_SUPPLY_SHORT: readonly string[] = [
  "Every other surface on P3 is open whether or not a student is matched.",
  "P3 does not guarantee a match, and nothing else waits on one.",
  "Approved mentors serve every institution on P3, not one at a time.",
];

/** The k-anonymity commitment, cut to two single-line sentences. */
export const SUPPRESSION_SHORT: readonly string[] = [
  `No figure representing fewer than ${SUPPRESSION_FLOOR} students is rendered anywhere.`,
  "Suppression runs on our servers, before the number reaches your browser.",
];

/**
 * The risk-reversal line, in Adobe's position: directly under the hero and
 * above the price, at body size, not as small print. Adobe's equivalent is 14
 * words and it is the second thing on their page.
 */
export const REASSURANCE = `Annual plans run ${TERM_MONTHS} months; cancel with ${CANCELLATION_NOTICE_DAYS} days notice before renewal. Monthly plans end at the end of the next full month.`;

/** Nine words. The full reasoning lives on /trust, not here.
 *
 *  UNUSED, DELIBERATELY. Thomas has now deleted this sentence from the page
 *  twice ("again", Sep 8 2026). It is kept as a constant rather than removed
 *  so the wording survives if it is ever wanted on /trust, and so nobody
 *  re-derives a fresh variant of it onto /pricing. Do not re-mount it there. */
export const CAPACITY_IN_A_LINE =
  "You choose your capacity. P3 does not meter and does not count.";

/**
 * The gateway FAQ. Five questions, and no answer runs past two lines.
 *
 * TWO WENT ON SEPTEMBER 8 2026, at Thomas's instruction. "What am I buying?"
 * was answered in full by the section directly above it, and an FAQ whose
 * first entry restates the page is an FAQ nobody reads. "Do you have SOC 2?"
 * went because the absence is not softened by being asked: it is item one of
 * NOT_OFFERED_SHORT and it is on /trust in full, which is where a security
 * reviewer looks for it.
 */
/* THE NO-GUARANTEE LINE IS NOT IN THIS LIST, AND THAT IS A DECISION.
   It lived on the Mentorship card until Sep 8 2026, moved here when that card
   was deleted, and was removed from here the same day at Thomas's request.
   It is the third time it has been asked to move. It still exists, in
   NOT_OFFERED / NOT_OFFERED_SHORT, which render on /trust.

   So: /pricing no longer states anywhere that P3 does not guarantee a match.
   That is Thomas's call and it is defensible, because the repositioning says
   the product does not wait on a match. It is recorded here because a
   disclosure that has been moved three times is one edit away from being lost
   entirely, and the next person to tidy /trust should know this is the only
   place it survives. */
export const GATEWAY_FAQ: readonly FaqItem[] = [
  {
    q: "What do students get before they have a mentor?",
    a: "Opportunities, pathways, open questions and video answers. Pulse AI is 18 and older.",
  },
  {
    q: "Do you bill per student?",
    a: "No. P3 does not meter claims for billing. Passing your band is a conversation at renewal, not a charge.",
  },
  {
    q: "Can we pay by purchase order?",
    a: `Yes. Request a quote and we send a W-9, remittance details and an invoice on Net ${INVOICE_NET_DAYS} terms.`,
  },
  {
    q: "Do you support single sign-on?",
    a: "No. Administrators sign in with a six digit code sent to their work email.",
  },
  {
    q: "What if we do not renew?",
    a: `Nothing is deleted. Your dashboard is export only for ${EXPORT_RETENTION_MONTHS} months, and students keep the platform.`,
  },
];

/* ==========================================================================
   6. How students claim
   ========================================================================== */

export interface ClaimStep {
  readonly n: string;
  readonly title: string;
  readonly body: string;
}

export const CLAIM_STEPS: readonly ClaimStep[] = [
  {
    n: "01",
    title: "Share your join link",
    body: "You get a link, a six character code and a QR code the hour your subscription starts. Put them in an advising email, an orientation deck, or on a wall.",
  },
  {
    n: "02",
    title: "Your students connect in the app",
    body: "A student opens the link or types the code, sees your institution's name and what you will be able to see, and confirms. It is one tap, and they can undo it at any time.",
  },
  {
    n: "03",
    title: "Reporting turns on at ten",
    body: `Your dashboard starts reporting once ${SUPPRESSION_FLOOR} students have claimed you. Below that it shows a progress meter and tells you how far there is to go, rather than showing you a number we have committed not to show.`,
  },
];

/* ==========================================================================
   7. Key terms, displayed on the checkout page rather than only linked

   The page's own text and the button label must themselves communicate what
   clicking does, and the acceptance area must not be cluttered. That design
   was reasoned under Illinois law, where Sgouros v. TransUnion (7th Cir. 2016)
   controlled. Governing law moved to Louisiana on Sep 12 2026, which sits in
   the Fifth Circuit: the principles are general to clickwrap, but counsel
   should confirm the analysis for the new forum. These are the clauses a buyer must be able to read without
   leaving the page. The full documents are linked at permanent versioned URLs.
   ========================================================================== */

export interface KeyTerm {
  readonly clause: string;
  readonly heading: string;
  readonly text: string;
}

/**
 * Each clause ref names the clause in the full Enterprise Subscription Terms
 * (src/app/lib/legal.ts) that carries this exact text. tests/legal.test.ts
 * asserts the text is there verbatim, so the pane and the contract cannot
 * disagree.
 */
export const KEY_TERMS: readonly KeyTerm[] = [
  {
    clause: "10.1",
    heading: "There is no service level agreement",
    text: "P3 does not offer a service level agreement. We do not commit to an uptime percentage and we do not offer service credits. We work to keep the dashboard available and we will tell your administrators in advance about planned maintenance where we reasonably can, and we are not going to promise a number we cannot presently measure and stand behind.",
  },
  {
    clause: "11.3",
    heading: "Small numbers are suppressed",
    text: `P3 suppresses or rolls up any figure in the dashboard, in an export or in a report that would represent fewer than ${SUPPRESSION_FLOOR} individuals. We apply it before the number reaches your browser, not after. If your claimed cohort is small, or a breakdown within it is small, many figures will show as suppressed rather than as a number. That is the design working correctly. It is not a defect and it is not a reason for a refund.`,
  },
  {
    clause: "14.3",
    heading: "No outcome is guaranteed",
    text: "We do not guarantee any enrollment, retention, graduation, persistence, scholarship, credential, employment or placement outcome, any level of student participation, or any improvement in any institutional metric. Dashboard figures describe what students did on the P3 platform. They do not establish that P3 caused any outcome.",
  },
  {
    clause: "15.1",
    heading: "Liability is capped, including privacy liability",
    text: "Neither party is liable for indirect, incidental, special, consequential, exemplary or punitive damages. Each party's total liability is capped at the greater of the fees paid in the twelve months before the claim or the floor stated in clause 15.2. Liability connected with privacy, confidentiality, security or data protection, including under the Data Sharing Addendum, sits inside that cap and is not uncapped.",
  },
  {
    clause: "7.1",
    heading: "Term and renewal",
    text: `An annual subscription runs for ${TERM_MONTHS} months and renews for a further ${TERM_MONTHS} months at the same band price unless you tell us at least ${CANCELLATION_NOTICE_DAYS} days before the renewal date. We email your administrators ${RENEWAL_NOTICE_DAYS[0]} days and ${RENEWAL_NOTICE_DAYS[1]} days before that date, and the second email names the deadline as a date. A monthly subscription renews each month at the same band price until you turn renewal off, and then ends at the end of the next full month. There is no automatic price increase.`,
  },
  {
    clause: "16.3",
    heading: "Either party may end it on sixty days notice",
    text: `Either party may terminate this agreement for any reason on ${CONVENIENCE_NOTICE_DAYS} days written notice. If you terminate for convenience, fees already paid are not refunded. If P3 terminates for convenience, P3 refunds the unused portion of prepaid fees, prorated to the termination date.`,
  },
  {
    clause: "16.5",
    heading: "What happens to your data if it ends",
    text: `Administrator access to live reporting ends, and your dashboard stays available in read-only export mode for ${EXPORT_RETENTION_MONTHS} months so you can retrieve what you need. Your students' access to the P3 app is not affected by the end of this agreement, ever. Students hold their own relationship with P3.`,
  },
  {
    clause: "17.1",
    heading: "Using each other's names",
    text: "You allow P3 to identify you by name and logo as a partner on our website, in a customer list, and in board, funder, grant and investor materials, following your published brand guidelines, and we stop within thirty days of your written request. Materials already distributed before your request do not have to be recalled. Anything beyond a name and a logo, including a quote, a case study or a press release, needs your prior written approval.",
  },
  {
    clause: "19.1",
    heading: `${LEGAL.governingLaw} law, and what a public institution should do instead`,
    text: `These Terms are governed by the laws of the State of ${LEGAL.governingLaw}, with venue in ${LEGAL.venue}. If you are a public institution and your state's law prohibits you from agreeing to that, or to the limitation of liability or the warranty disclaimers, do not accept online. Write to ${LEGAL.contactEmail} and we will route you to a signed agreement.`,
  },
];

/** The two questions in the routing gate. A yes on the second is a stop. */
export const ROUTING_QUESTIONS = {
  publicInstitution:
    "Are you a public institution, a state agency, or an instrumentality of a state?",
} as const;

/* ==========================================================================
   8. FAQ, written as a procurement officer actually asks it
   ========================================================================== */

export interface FaqItem {
  readonly q: string;
  readonly a: string;
}

export const FAQ: readonly FaqItem[] = [
  {
    q: "What exactly am I buying?",
    a: "A capacity commitment and the dashboard that comes with it. You pick the band that fits your program and that band is the whole price, by the month or for the year. You are not buying your students' access to P3, which is free and stays free either way.",
  },
  {
    q: "Do you count our students and bill us for them?",
    a: "No. P3 does not meter claims for billing. The dashboard shows you how many students have claimed your institution, because that is useful to you, and that number is never an input to an invoice. If it grows past your band we look at it together at renewal.",
  },
  {
    q: "Can we pay by purchase order?",
    a: `Yes. Request a quote from this page and you get a PDF with a quote number, valid ${QUOTE_VALID_DAYS} days, plus our W-9 and our ACH and check remittance details. Send us the purchase order number and we issue an invoice on Net ${INVOICE_NET_DAYS} terms. Your dashboard turns on the day the invoice is sent, not the day the payment clears.`,
  },
  {
    q: "Can you align the term to our fiscal year?",
    a: "Yes. Tell us your fiscal year end when you request the quote and we will start the term where you need it, so every renewal after that lands with your budget.",
  },
  {
    q: "How long does your quote hold?",
    a: `${QUOTE_VALID_DAYS} days, and longer if you ask. A university purchase order commonly takes four to eight weeks from decision to signature, so if your term starts in the fall it is worth starting the quote in the spring.`,
  },
  {
    q: "Do you have SOC 2?",
    a: "No. P3 does not hold a SOC 2 report and has not commissioned a third party penetration test. We run on AWS and Cloudflare, we publish our subprocessors with their data regions, we will complete your security questionnaire, and we will support an assessment of the institutional surface, which is one Cloudflare Worker and one React application. If your process requires SOC 2 today, tell us at the start rather than at contract stage.",
  },
  {
    q: "Do you support single sign-on?",
    a: "No. Administrators sign in with a six digit single-use code sent to their work email, at a domain your team already uses. We use a code rather than a clickable link because university mail security often follows links automatically and would spend the link before your administrator did. The upgrade path is Cloudflare Access, which brokers SAML and OIDC, and we will not give you a date for it unless we are willing to be held to it.",
  },
  {
    q: "Do you connect to our student information system or our LMS?",
    a: "No. P3 does not read from or write to either one, and does not ask for access to either one. The join link and the QR code are copy and paste.",
  },
  {
    q: "How do you know these are our students?",
    a: "We do not verify it, and we would rather say so plainly. A student asserts the affiliation themselves, from your join link or code, or by picking your institution in the P3 app. That is not an enrollment check, and P3 has no roster feed that could provide one.",
  },
  {
    q: "What will we actually see in the first term?",
    a: `A claimed roster count and how it is growing, how many students have been matched with a mentor, how many asked for one and are still waiting, and what your students say they want help with. Graduation and first role appear only where a student volunteered them, and P3 does not verify them against your records. Any figure representing fewer than ${SUPPRESSION_FLOOR} students is not shown, so a new cohort will see a progress meter before it sees a breakdown.`,
  },
  {
    q: "Who owns the data, and what does our browser receive?",
    a: "Your institution's records stay yours, your students' accounts stay theirs, and P3 sells data to no one. The one list about people your browser receives is who has joined: first name and last initial, when they joined and whether they have a mentor. Every figure is aggregated and suppressed on our servers, and no endpoint that returns your analytics accepts an organization identifier, because the organization is read from your server-side session.",
  },
  {
    q: "What happens if we do not renew?",
    a: `Nothing is deleted. Your dashboard becomes export-only for ${EXPORT_RETENTION_MONTHS} months so you can pull what you need on your own timetable, and we keep your tenant so you can resume in place rather than start over. Your students keep their accounts, their mentors and their conversations, because those belong to them and not to the subscription.`,
  },
];

/* ==========================================================================
   9. Trust page content
   ========================================================================== */

/**
 * "published": on this site now, at the address in `href`. "on-request": real
 * and sent by email, not published openly. "in-preparation": does not exist
 * yet, and the page says so rather than linking to nothing.
 */
export type DocumentStatus = "published" | "on-request" | "in-preparation";

export interface TrustDocument {
  readonly name: string;
  readonly detail: string;
  readonly status: DocumentStatus;
  readonly href?: string;
}

/**
 * STATUSES CORRECTED Sep 9 2026. Until then the W-9, the IRS letter and the
 * Accessibility Conformance Report were listed as published with Download
 * links to /documents/*.pdf, and no such files existed: the links returned
 * the app shell with HTTP 200. The W-9 and the IRS letter are real documents
 * P3 sends on request, so they say so; they flip back to "published" the day
 * the PDFs are placed under public/documents/ at these exact paths, and
 * tests/legal.test.ts refuses a "published" document whose file is missing.
 * No VPAT has been written yet, so the report says "in preparation" rather
 * than claiming a self-assessment that has not happened.
 *
 * PUBLISHED Sep 13 2026. The W-9, signed by Thomas that day, and the IRS
 * letter (Letter 4425 of Feb 15 2024, which Thomas confirms is the
 * determination letter P3 sends) now sit under public/documents/ at these
 * paths. Both were read in full before they were placed: no scripts, no
 * embedded files, and only the IRS forms' own metadata.
 */
export const TRUST_DOCUMENTS: readonly TrustDocument[] = [
  {
    name: "W-9",
    detail: `Signed, 2026. EIN ${LEGAL.ein}.`,
    status: "published",
    href: LEGAL.w9Url,
  },
  {
    name: "IRS 501(c)(3) determination letter",
    detail: "Establishes P3's exempt status for your supplier record.",
    status: "published",
    href: "/documents/p3-irs-determination-letter.pdf",
  },
  {
    name: "Enterprise Subscription Terms",
    detail: `Version ${LEGAL.termsVersion}, at a permanent URL that is never edited after publication.`,
    status: "published",
    href: LEGAL.termsUrl,
  },
  {
    name: "Data Sharing Addendum",
    detail: `Version ${LEGAL.dsaVersion}. Section V carries the ${SUPPRESSION_FLOOR} student privacy floor.`,
    status: "published",
    href: LEGAL.dsaUrl,
  },
  {
    name: "Subprocessor list",
    detail: "Every vendor that processes personal data, with its purpose and its region.",
    status: "published",
    href: "#subprocessors",
  },
  {
    name: "Accessibility Conformance Report",
    detail: "VPAT 2.5 WCAG, self-assessed. In preparation, and when it is published it will say which criteria we do not meet.",
    status: "in-preparation",
    href: "/documents/p3-vpat-2-5-wcag.pdf",
  },
  {
    name: "Certificate of Insurance",
    detail: "Commercial general liability and cyber liability. Your institution can be named.",
    status: "on-request",
  },
  {
    name: "HECVAT 4.1",
    detail: "Completed for your institution on request, and sent with the answers rather than with a promise to answer.",
    status: "on-request",
  },
  {
    name: "Security questionnaire response",
    detail: "Your own form, completed. We do not require you to use ours.",
    status: "on-request",
  },
];

export interface Absence {
  readonly title: string;
  readonly body: string;
}

export const ABSENCES: readonly Absence[] = [
  {
    title: "SOC 2",
    body: "P3 does not hold a SOC 2 report. We are a nonprofit with a small engineering surface, we run on AWS and Cloudflare, we publish our subprocessors, and we will complete your security questionnaire. If your process requires SOC 2 today, we would rather you tell us now than at contract stage.",
  },
  {
    title: "A third party penetration test",
    body: "P3 has not commissioned one. The institutional surface is one Cloudflare Worker and one React application, which is small enough to audit by reading, and P3 will support a customer-run assessment of it.",
  },
  {
    title: "Single sign-on",
    body: "There is no SAML, OIDC or SCIM. Administrators sign in with a six digit single-use code sent to their work email. The stated upgrade path is Cloudflare Access, which brokers SAML and OIDC on infrastructure P3 already uses, and no date is given because none has been committed.",
  },
  {
    title: "A public status page and a service level agreement",
    body: "P3 publishes no status page, commits to no uptime percentage, and offers no service credits. We will tell your administrators in advance about planned maintenance where we reasonably can.",
  },
  {
    title: "An API, or an SIS or LMS connection",
    body: "There is no public API and no connector to a student information system or a learning management system. Nothing P3 sells requires access to either one.",
  },
];

export interface Subprocessor {
  readonly name: string;
  readonly purpose: string;
  readonly region: string;
}

export const SUBPROCESSORS: readonly Subprocessor[] = [
  {
    name: "Amazon Web Services, Inc.",
    purpose: "Core platform infrastructure and the application database.",
    region: "United States",
  },
  {
    name: "Cloudflare, Inc.",
    purpose:
      "Edge compute and databases for the enterprise gateway, Pulse AI, Conversations and the web app. Network security and content delivery.",
    region: "United States, with transient edge processing worldwide",
  },
  {
    name: "Anthropic, PBC",
    purpose:
      "AI features: the Pulse assistant, Discover, CV review, and automated moderation of mentor and student messages.",
    region: "United States",
  },
  {
    name: "Algolia, Inc.",
    purpose:
      "Search indexes for mentors, students, opportunities, posts and questions. This is the index the institutional dashboard reads.",
    region: "United States",
  },
  {
    name: "Google LLC (Firebase)",
    purpose: "Mobile analytics, crash reporting and deep links.",
    region: "United States",
  },
  {
    name: "Amplitude, Inc.",
    purpose: "Product analytics.",
    region: "United States",
  },
  {
    name: "Retool, Inc.",
    purpose: "Internal administration console used by P3 staff.",
    region: "United States",
  },
  {
    name: "Twilio Inc. (SendGrid)",
    purpose: "Transactional email.",
    region: "United States",
  },
  {
    name: "Resend",
    purpose: "Administrator sign-in codes and safeguarding alert delivery.",
    region: "United States",
  },
  {
    name: "Intercom, Inc.",
    purpose: "Customer messaging and support.",
    region: "United States",
  },
  {
    name: "Functional Software, Inc. (Sentry)",
    purpose: "Error and crash monitoring, including session replay.",
    region: "United States",
  },
  {
    name: "Stripe, Inc.",
    purpose: "Payments, invoicing and quotes. Stripe never receives student data.",
    region: "United States",
  },
  {
    name: "3Advance, LLC",
    purpose: "Engineering delivery partner for the P3 platform.",
    region: "United States",
  },
];

/** The residency sentence, written to match P3's actual configuration. */
export const DATA_RESIDENCY = `P3 stores personal data governed by the Data Sharing Addendum in United States regions of its cloud providers, and P3's subprocessors are entities organized in the United States. Certain edge computing, content delivery and network security functions may process data in transit at points of presence outside the United States. That processing is transient, is incidental to delivering the service, and does not result in personal data being stored outside the United States.`;

export interface HoldingRow {
  readonly label: string;
  readonly body: string;
}

/** What P3 holds about a student, and what it does not. */
export const WHAT_P3_HOLDS: readonly HoldingRow[] = [
  {
    label: "The account a student created",
    body: "Name, email, and the profile fields a student filled in themselves: education goal, the help they are looking for, industry interest and meeting rhythm.",
  },
  {
    label: "What a student did on P3",
    body: "Mentor matches, conversations, milestone check-ins, saved opportunities and app activity.",
  },
  {
    /* ADDED Sep 9 2026. This row was missing, and its absence is why the
       "does not hold" column was able to say P3 held no outcome data at all
       without anything contradicting it on the same screen. */
    label: "What a student told us about themselves",
    body: "Outcome milestones require a note before they complete, so a student says in their own words where they started work or which major they chose. The career timeline holds the education and roles they entered, with organization and years. All of it is volunteered, and none of it is verified.",
  },
  {
    label: "The institution a student claimed",
    body: "One organization identifier, set by the student, from your join code or link, and removable by the student at any time.",
  },
];

export const WHAT_P3_DOES_NOT_HOLD: readonly HoldingRow[] = [
  {
    label: "Anything from your systems",
    body: "No student information system record, no learning management system record, no roster, no transcript, no financial aid record. P3 has no feed from any of them and asks for access to none of them.",
  },
  {
    /* CORRECTED Sep 9 2026. The previous version read "no graduation, degree
       conferral, GPA ... there is no field anywhere in P3's systems that could
       contain one", and that was wrong in a way that UNDERSOLD the product.

       P3 does capture graduation and first-role, as student-volunteered data:
       the milestone framework carries those nodes, outcome-bearing milestones
       set `completionNoteRequired` so a student must say where they started
       work or what major they chose before the milestone completes, and the
       Career Timeline holds education and role entries with organisation and
       years. What P3 does NOT have is a VERIFIED record from an authority.
       The distinction is the whole sentence, and the absence is real without
       being absolute. */
    label: "A verified outcome record",
    body: "P3 holds no registrar or employer record. Graduation, major, first role and employer reach P3 only when a student volunteers them, in a milestone or their own career timeline, and P3 does not verify any of it against your systems or anyone else's. There is no GPA, no credit hours, no salary, no credential award from an awarding body, and no enrollment verification.",
  },
  {
    label: "Demographics or location",
    body: "No race, ethnicity, first generation status, Pell eligibility or home address. P3's records carry no location field. Analytics city and region are derived from a device's network address and are never treated as where a student lives.",
  },
  {
    label: "Any cost data about your institution",
    body: "P3 holds none, which is why no dollar figure of any kind appears anywhere in the dashboard.",
  },
];

/** Plain language on the suppression commitment. */
export const SUPPRESSION_EXPLAINED: readonly string[] = [
  `No figure representing fewer than ${SUPPRESSION_FLOOR} students appears anywhere in the partner dashboard or in any export. Where showing the remaining figures would let a hidden one be worked out by subtraction, we hide a second one as well. This is the same threshold state education agencies use in public reporting, and it is a commitment in your Data Sharing Addendum rather than a setting somebody at P3 can change.`,
  `Both the aggregation and the suppression happen on P3's servers. A suppressed value never reaches your browser, because there is no view and no export that contains one. There is also no individual level export, deliberately and permanently, because offering one would defeat the commitment we just made you.`,
  `The practical consequence, stated up front rather than discovered later: a small cohort will see totals and a growth curve, and will see most breakdowns held back until the cohort grows. The dashboard tells you how many students each view still needs.`,
];

/**
 * The roster of who has joined (Sep 17 2026), said next to the suppression
 * commitment so the two are read together. Not part of any published
 * document: 2026.2 carries its own words (legal.ts).
 */
export const ROSTER_EXPLAINED: readonly string[] = [
  "One list on the dashboard is about people rather than figures: Enrollment, who has joined. For an institution registered from September 17, 2026, it shows each student by first name and last initial, when they joined, and whether they have a mentor, and the mentors who name the institution on their profile. Students are told this on the join screen before they join.",
  "The roster carries no email address, phone number, message or free text, is shown to owner and admin accounts only, and is not exported. What a student does in P3 is never shown for one person: every activity figure follows the floor above.",
];

/** Dated change log for the trust page. Newest first. */
export interface TrustUpdate {
  readonly date: string;
  readonly body: string;
}

export const TRUST_UPDATES: readonly TrustUpdate[] = [
  {
    date: "September 19, 2026",
    body: "Three corrections. Invitations are limited to email domains someone active on your team already uses; P3 does not check domains by DNS record, as this page had said. A student can also pick your institution in the P3 app's profile, which asks them only to confirm, without the join screen's lists. Failed code attempts are not in your institution's audit trail, which owners and admins now download from Settings.",
  },
  {
    date: "September 17, 2026",
    body: "Enrollment: institutions registered from this date see each student by first name and last initial, when they joined and whether they have a mentor, and the mentors who name the institution. Version 2026.2 of the Terms and the Data Sharing Addendum, and the join screen, say so; the reporting floor and the school official position are unchanged.",
  },
  {
    date: "September 8, 2026",
    body: "An AI features section was added, stating that Pulse AI and the CV review are limited to students aged 18 and older, that the gate is enforced at render and at the request, and that Pulse can only link to a curated list.",
  },
  {
    date: "September 7, 2026",
    body: "Subprocessor list expanded to name Cloudflare, Anthropic, Algolia, Resend and Stripe, each of which processes personal data today and none of which appeared on the previous schedule.",
  },
  {
    date: "September 7, 2026",
    body: "The data residency statement was rewritten to describe P3's actual configuration, including transient edge processing outside the United States.",
  },
  {
    date: "September 7, 2026",
    body: "Partner dashboard sign-in moved to six digit single-use codes on verified institutional domains, with server-side revocable sessions.",
  },
];

/* ==========================================================================
   10. Living from the Worker's manifest

   The Worker publishes its own capability manifest at
   GET /api/enterprise/capabilities (src/worker/lib/capabilities.ts) and on
   the session. Settings renders it, under Data and methods, through the
   function below: a signed-in institution from its session, the sample
   dashboard from the public route. There is no fallback to the typed
   constant in section 4. When the manifest cannot be read, the row says so,
   because a customer shown a stale copy of what P3 offers has been told
   something the served contract does not say.

   The two shapes differ on purpose and the difference is honest. The build
   plan splits day one from the ninety day mark, because a buyer wants to know
   what is there the hour they sign. The Worker splits shipped from planned,
   because an API must never call a planned thing available. The manifest's
   split is the one rendered, so no screen promises a date the Worker has not
   committed to.
   ========================================================================== */

export interface CapabilityManifestShape {
  readonly shipped: readonly string[];
  readonly plannedFrom: ReadonlyArray<{ item: string; date: string | null }>;
  readonly notOffered: readonly string[];
  readonly suppression: { readonly floor: number; readonly statement: string };
}

export function capabilityColumnsFrom(
  manifest: CapabilityManifestShape,
): readonly CapabilityColumn[] {
  return [
    {
      id: "day-one",
      eyebrow: "Working today",
      heading: "What is working today",
      note: "Available the hour your subscription starts.",
      items: manifest.shipped,
    },
    {
      id: "by-90-days",
      eyebrow: "Planned",
      heading: "Planned, and not yet built",
      note: "A date appears only where P3 has committed to one. No date means we intend to build it and will not name a day we cannot be held to.",
      items: manifest.plannedFrom.map((p) =>
        p.date === null ? `${p.item} No date committed.` : `${p.item} From ${p.date}.`,
      ),
    },
    {
      id: "not-offered",
      eyebrow: "Not offered",
      heading: "What P3 explicitly does not offer",
      note: "We would rather you read this here than find it in a security review.",
      items: manifest.notOffered,
    },
  ];
}
